import WidgetBase from '../widget.base';
import {
    each,
    mq
} from '../../lib/util';
import initProductService, {
    addProductListener,
    PRODUCTS_PER_PAGE,
    filterModel,
    getProducts,
    ORDER_DEFAULT,
    SORT_ALLOWED,
    ORDER_ASC,
    ORDER_DESC,
    updateFilterModel,
    resetFilterModel,
    SORT_ALLOWED_AEN,
    updateUrlForFilterModel
} from '../../services/product';
import compileProductListings from './product-list.template.pug';
import compileProductListHeader from './product-list-header.template.pug';
import {
    find
} from 'lodash-es';
import Pager from '../../components/pager/pager';
import Filters from '../../components/pager/filters';

const DRAWING_SELECTOR = '[data-drawing]';
const SORT_SELECTOR = '[data-sort]';
const SUBCAT_BREAKPOINT = 'lg';

export default class Subcategory extends WidgetBase {
    static selector = '[data-js-subcategory]';
    products;
    facets;
    totalPages;
    totalProducts;
    $activeSort = null;
    listFrag = null;
    listingType = null;

    constructor(el) {
        super(el);

        const pagerEl = this.el.querySelector('[data-pager]'),
            filtersWrap = this.el.querySelector('[data-filters]');

        this.results = document.querySelector('[data-results]');
        this.stickyElement = document.querySelector('[data-results-sticky]');
        this.stickyTop = document.querySelector('[data-results-top]');
        this.body = document.querySelector('body');
        this.header = document.querySelector('[data-js-header]');

        this.productList = this.el.querySelector('[data-product-list]');
        this.sortBar = this.el.querySelector('[data-sort-bar]');
        this.listingType = this.getFromDom('jsListingType') || 'Common';

        this.initialModel = {
            productCategory: this.getFromDom('jsSubcategory'),
            primary1: this.getFromDom('jsPrimary1'),
            primary2: this.getFromDom('jsPrimary2'),
            primary3: this.getFromDom('jsPrimary3')
        };

        initProductService(this.initialModel, this.listingType);

        // Initialize the sort bar.
        this.renderSortBar();
        this.updateSortBar();

        this.filters = new Filters(filtersWrap, {
            onFilter: (name, value, action) => {
                updateFilterModel(name, value, action);
                getProducts();
            },
            onReset: () => {
                setTimeout(() => {
                    resetFilterModel();
                    getProducts();
                });
            }
        });

        // Update the filters to reflect the initial model state.
        this.filters.updateFilters(filterModel);

        this.pager = new Pager(pagerEl, {
            perPage: PRODUCTS_PER_PAGE,
            initialPage: filterModel.page
        });

        this.pager.listen('page:change', (e) => {
            filterModel.page = e.detail;

            // Update the URL to reflect the current page.
            updateUrlForFilterModel();

            // Remember the scroll position before updating the product list.
            this.restoreScrollPosition = true;
            let bodyHeight = this.body.offsetHeight,
                windowBottom = window.pageYOffset + window.innerHeight;
            this.distanceToBottom = bodyHeight - windowBottom;

            // Get the new product list.
            getProducts();

        });

        this.productList.addEventListener('click', this.handleProductListClick.bind(this));
        this.sortBar.addEventListener('click', this.handleSortBarClick.bind(this));

        let productsListener = (data) => {
            this.products = data.results;
            this.facets = data.facets;

            // If the number of results have been reduced such that none appear on the current page any
            // longer, go to the last page that does show results. The server API mirrors this same logic,
            // which is why data.results will contain the appropriate products.
            const lastVisiblePage = Math.trunc(data.count / PRODUCTS_PER_PAGE) + 1;
            filterModel.page = Math.min(lastVisiblePage, filterModel.page);

            this.pager.update(filterModel.page, data.count);

            this.renderProductList();
            this.toggleFilterOptions();

            // Update the scroll position to what it was before using the pager?
            if (this.restoreScrollPosition) {
                this.restoreScrollPosition = false;

                // Wait a moment to be sure the page has a chance to update the layout before
                // recalculating the scroll position.
                setTimeout(() => {
                    let bodyHeight = this.body.offsetHeight,
                        windowBottom = window.pageYOffset + window.innerHeight,
                        currentDistanceToBottom = bodyHeight - windowBottom,
                        verticalScroll = currentDistanceToBottom - this.distanceToBottom;
                    window.scrollBy(0, verticalScroll);
                }, 0);

            }

        };

        addProductListener(productsListener);

        setTimeout(() => {
            this.loaded();
        });

        // When the user clicks back/forward in their browser.
        window.addEventListener('popstate', (event) => {

            // Re-initialize the model and get the products.
            let setupModel = event.state || this.initialModel;
            initProductService(setupModel, this.listingType);

            // Update the filter.
            this.filters.updateFilters(filterModel);

            // Update the sort bar.
            this.updateSortBar();

            // Listen for updates to the products data.
            addProductListener(productsListener);

        });

        this.setupStickyHandlers();
    }

    /**
     * Returns the maximum gap that can be set for the sticky results.
     * @returns {number} The maximum gap.
     */
    getMaxStickyGap() {
        const containerHeight = this.results.offsetHeight,
            elementHeight = this.stickyElement.offsetHeight,
            buffer = 5,
            maxTop = containerHeight - elementHeight - buffer;
        return Math.max(0, maxTop);
    }

    /**
     * Adjusts the results so they remain visible in the viewport.
     */
    adjustStickyResults() {

        // Variables.
        const maxTop = this.getMaxStickyGap(),
            elementTop = this.stickyTop.getBoundingClientRect().top,
            windowScroll = window.pageYOffset,
            bodyTop = this.body.getBoundingClientRect().top,
            headerHeight = this.header.offsetHeight,
            topBuffer = 10,
            distanceFromHeader = elementTop + windowScroll + bodyTop - headerHeight - topBuffer;
        let positiveDistance = Math.abs(distanceFromHeader);

        // Do the results need to be moved down below the header?
        if (distanceFromHeader < 0) {

            // Move the results down, but not so far down they extend beyond the bottom of the containing element.
            if (positiveDistance > maxTop) {
                positiveDistance = maxTop;
            }
            this.stickyElement.style.top = Math.round(positiveDistance).toString() + 'px';

        } else {
            this.stickyElement.style.top = '0';
        }

    }

    /**
     * Setup the event handlers for the sticky results.
     */
    setupStickyHandlers() {

        // Variables.
        const handler = this.adjustStickyResults.bind(this);

        // Adjust the results on window scroll and resize events.
        window.addEventListener('scroll', handler);
        window.addEventListener('resize', handler);

        // Also adjust the results every so often (e.g., in case the filters have been expanded/collapsed or something
        // else on the page caused the layout to change in some way that affects the sticky results).
        setInterval(handler, 1000);

    }

    getFromDom(attr) {
        return this.el.dataset[attr];
    }

    renderSortBar() {
        let isCommon = this.listingType === 'Common',
            isAen = this.listingType === 'Specialized - Application Engineering Notes';
        const productListHeader = compileProductListHeader({
            sorts: isCommon
                ? SORT_ALLOWED
                : (isAen
                    ? SORT_ALLOWED_AEN
                    : null),
            filterModel: filterModel,
            listingType: this.listingType,
            terms: window.terms,
        });

        this.sortBar.innerHTML = productListHeader;
        this.$activeSort = $(this.sortBar.querySelector(SORT_SELECTOR + '.active'));

        setTimeout(() => {
            this.setSortColumnWidths();
        }, 200);
    }

    setSortColumnWidths() {
        if (mq(SUBCAT_BREAKPOINT)) {

            const sortBarButtons = this.sortBar.querySelectorAll('[data-sort]');

            each(sortBarButtons, (button) => {
                const column = button.parentElement;
                column.style.width = `${column.offsetWidth}px`;
            })
        }
    }

    renderProductList() {
        this.productList.innerHTML = compileProductListings({
            products: this.products,
            listingType: this.listingType,
            terms: window.terms,
        });
    }

    /**
     * Toggles the filter options so that they will either appear as enabled or disabled (depending on
     * whether or not selecting them would display zero results).
     */
    toggleFilterOptions() {

        // Variables.
        let facets = this.facets, facet,
            filtersWrap = this.el.querySelector('[data-filters]'),
            inputs = filtersWrap.querySelectorAll('input'),
            keys = Object.keys(facets),
            i, j, input,
            values, value;

        // Disable all inputs that are not checked (we still want to allow expanding of criteria,
        // so it will still be enabled if it's already checked).
        for (i = 0; i < inputs.length; i++) {
            input = inputs[i];
            if (!input.checked) {
                input.disabled = true;
                input.parentNode.classList.add('disabled');
            }
        }

        // Enable inputs that match a facet.
        for (i = 0; i < keys.length; i++) {
            facet = keys[i];
            values = facets[facet];
            for (j = 0; j < values.length; j++) {
                value = values[j];
                input = filtersWrap.querySelector(`input[name="${facet}"][value="${value}"]`);
                if (input) {
                    input.disabled = false;
                    input.parentNode.classList.remove('disabled');
                }
            }
        }

    }

    handleProductListClick(e) {

        const $target = $(e.target);

        if ($target.is(DRAWING_SELECTOR)) {
            return;
        }

        const $product = $(e.target).closest('[data-product]');

        if ($product.length) {
            const id = $product.data('product');
            const product = find(this.products, _product => _product.id === id);
        }
    }

    handleSortBarClick(e) {
        const $target = $(e.target);

        if (!$target.is(SORT_SELECTOR)) {
            return;
        }

        const {
            sort,
            order
        } = this.sortAndOrder($target.data('sort'));

        if (this.$activeSort !== null && this.$activeSort.data('sort') !== sort) {
            this.$activeSort.removeClass('active asc desc');
        }
        $target.removeClass('desc asc');
        $target.addClass('active ' + order);
        this.$activeSort = $target;

        getProducts();
    }

    /**
     * Updates the sort bar to reflect the sort order currently in the filter model.
     */
    updateSortBar() {
        let sort = filterModel.sort,
            order = filterModel.order;
        let sortEls = document.querySelectorAll(SORT_SELECTOR);
        each(sortEls, x => {
            x.classList.remove('asc');
            x.classList.remove('desc');
            x.classList.remove('active');
            if (x.getAttribute('data-sort') === sort) {
                x.classList.add('active');
                x.classList.add(order);
            }
        });
    }

    sortAndOrder(targetSort) {
        const currentSort = filterModel.sort;
        const currentOrder = filterModel.order;

        let targetOrder = ORDER_DEFAULT;

        if (targetSort === currentSort) {
            targetOrder = currentOrder === ORDER_ASC ? ORDER_DESC : ORDER_ASC;
        }

        filterModel.sort = targetSort;
        filterModel.order = targetOrder;

        // Update the URL to reflect the sort order.
        updateUrlForFilterModel();

        return {
            sort: targetSort,
            order: targetOrder
        };
    }
}
